import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiOutlineUserCircle, HiOutlineKey, HiOutlineEnvelope, HiOutlineArrowLeftCircle, HiOutlineEye, HiOutlineEyeSlash } from 'react-icons/hi2';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import TopBar from '../components/TopBar';
// Password field with eye toggle inside the input
const PwField = ({ value, onChange, show, setShow, required = true }) => (
  <div style={{ position: 'relative' }}>
    <input
      className="input"
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      required={required}
      minLength={6}
      style={{ paddingRight: 40 }}
    />
    <button
      type="button"
      onClick={() => setShow(s => !s)}
      tabIndex={-1}
      style={{
        position: 'absolute',
        right: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text-muted)',
        padding: 4,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {show ? <HiOutlineEyeSlash size={18} /> : <HiOutlineEye size={18} />}
    </button>
  </div>
);
const Profile = () => {
  
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();

  const [profileForm, setProfileForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const onSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const r = await api.put('/profile', profileForm);
      if (r.data.success) {
        toast.success('Profile updated');
        await refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const onChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
    if (pwForm.newPassword.length < 6) { toast.error('Password must be 6+ chars'); return; }
    setSavingPw(true);
    try {
      const r = await api.put('/profile/password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      if (r.data.success) {
        toast.success('Password updated');
        setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title="My Profile" />
      <div className="page">
        <div className="page-container" style={{ maxWidth: 720 }}>
          <div className="page-header">
            <div>
              <button className="btn btn-ghost btn-sm" onClick={() => nav(-1)}>
                <HiOutlineArrowLeftCircle size={16} /> Back
              </button>
              <h1 className="page-title" style={{ marginTop: 8 }}>My Profile</h1>
              <p className="page-subtitle">Edit your account details and password</p>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlineUserCircle size={20} color="var(--primary-700)" /> Account
            </h3>
            <form onSubmit={onSaveProfile}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Full name</label>
                <input className="input" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} required />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Email</label>
                <input className="input" type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} required />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </form>
          </div>

          <div className="card">
            <h3 style={{ margin: '0 0 16px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlineKey size={20} color="var(--primary-700)" /> Change Password
            </h3>
            <form onSubmit={onChangePw}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Current password</label>
                <PwField
                  value={pwForm.currentPassword}
                  onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))}
                  show={showCurrent}
                  setShow={setShowCurrent}
                />
              </div>
              <div className="grid grid-cols-2">
                <div>
                  <label className="label">New password</label>
                  <PwField
                    value={pwForm.newPassword}
                    onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
                    show={showNew}
                    setShow={setShowNew}
                  />
                </div>
                <div>
                  <label className="label">Confirm new password</label>
                  <PwField
                    value={pwForm.confirm}
                    onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                    show={showConfirm}
                    setShow={setShowConfirm}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button type="submit" className="btn btn-primary" disabled={savingPw}>{savingPw ? 'Saving…' : 'Update password'}</button>
              </div>
            </form>
          </div>

          {/* <div style={{ marginTop: 20, padding: 14, background: 'var(--primary-50)', borderRadius: 12, border: '1px solid var(--primary-200)', fontSize: 13, color: 'var(--primary-800)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <HiOutlineEnvelope size={18} />
            <span>Your role is <b style={{ textTransform: 'capitalize' }}>{user?.role}</b>. To change your role, ask the owner.</span>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Profile;
